import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import PanToolIcon from '@material-ui/icons/PanTool';
import CreateIcon from '@material-ui/icons/Create';
import OpenWithIcon from '@material-ui/icons/OpenWith';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import cn from 'classnames';
import TapeDrawPad from './TapeDrawPad';
import {
  TYPE_COLORS,
  edgeLengths,
  getFeatureTypeCode,
  getGarageKindName,
  labelAnchor,
  livingSqft,
  measureSqft,
  orthoSnap,
  roundPt,
  sectionAbbrev,
  hitFeature,
  snapToExisting,
  step,
  stretchEdge,
} from './tapeUtils';
import styles from './TapeCanvas.styles';

const useStyles = makeStyles(styles);

const TapeCanvas = ({
  features,
  selectedId,
  tapeConfig,
  onCommit,
  onSelect,
  onMove,
  onMoveStart,
  onClearFeatures,
  onDelete,
  onUndoFeatures,
  onRedoFeatures,
}) => {
  const classes = useStyles();
  const wrap = useRef(null);
  const [pxPerFt, setPxPerFt] = useState(10);
  const [origin, setOrigin] = useState({ x: 120, y: 280 });
  const [path, setPath] = useState([]);
  const [intent, setIntent] = useState('pan');
  const [length, setLength] = useState('24');
  const [dir, setDir] = useState('E');
  const [width, setWidth] = useState('40');
  const [depth, setDepth] = useState('28');
  const [edit, setEdit] = useState(null);
  const editRef = useRef(null);
  editRef.current = edit;
  const [view, setView] = useState({ w: 800, h: 600 });
  const hasCenteredRef = useRef(false);

  useEffect(() => {
    const el = wrap.current;
    if (!el) {
      return undefined;
    }
    const measure = () => setView({ w: el.clientWidth || 800, h: el.clientHeight || 600 });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Center the view on the existing sketch (if there is one) the first time it loads,
  // rather than always opening at a fixed default origin — a converted/mobile-drawn
  // sketch can sit anywhere in feet-space, so the default origin can leave it off-screen.
  useEffect(() => {
    if (hasCenteredRef.current) {
      return;
    }
    const pts = features.flatMap(f => f.vertices || []);
    if (!pts.length) {
      return;
    }
    hasCenteredRef.current = true;
    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const spanX = Math.max(maxX - minX, 8);
    const spanY = Math.max(maxY - minY, 8);
    const padding = 60;
    const availW = Math.max(view.w - padding * 2, 100);
    const availH = Math.max(view.h - padding * 2, 100);
    const fitScale = Math.min(availW / spanX, availH / spanY);
    const nextPxPerFt = Math.min(40, Math.max(2, fitScale));
    const midX = (minX + maxX) / 2;
    const midY = (minY + maxY) / 2;
    setPxPerFt(nextPxPerFt);
    setOrigin({
      x: view.w / 2 - midX * nextPxPerFt,
      y: view.h / 2 + midY * nextPxPerFt,
    });
  }, [features, view.w, view.h]);

  // Mode shortcuts: P(an)/E(dit)/D(raw)/A(ttach) — ignored while typing in a field (e.g. the
  // wall-length input) or with a modifier held, so they don't steal browser/OS shortcuts.
  useEffect(() => {
    const onKeyDown = e => {
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) {
        return;
      }
      switch (e.key.toLowerCase()) {
        case 'p':
          setIntent('pan');
          onSelect(null);
          break;
        case 'e':
          setIntent('edit');
          break;
        case 'd':
          setIntent('draw');
          onSelect(null);
          break;
        case 'a':
          if (selectedId) {
            setIntent('attach');
          }
          break;
        default:
          return;
      }
      e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedId, onSelect]);

  const screen = p => ({
    x: origin.x + p.x * pxPerFt,
    y: origin.y - p.y * pxPerFt,
  });

  const toFeet = (clientX, clientY) => {
    const rect = wrap.current?.getBoundingClientRect();
    const x = clientX - (rect?.left ?? 0);
    const y = clientY - (rect?.top ?? 0);
    return {
      x: (x - origin.x) / pxPerFt,
      y: (origin.y - y) / pxPerFt,
    };
  };

  const addWall = (d, ft) => {
    const n = Math.max(1, Math.round(ft));
    if (!(n > 0)) {
      return;
    }
    setPath(p => {
      const start = p.length ? p[p.length - 1] : { x: 0, y: 0 };
      const base = p.length ? p : [{ x: 0, y: 0 }];
      return [...base, step(start, d, n)];
    });
    setDir(d);
    setIntent('draw');
  };

  const close = () => {
    if (path.length < 3) {
      return;
    }
    onCommit(path);
    setPath([]);
    setIntent('draw');
  };

  const undo = () => {
    if (path.length) {
      setPath(p => p.slice(0, -1));
      return;
    }
    if (onUndoFeatures) {
      onUndoFeatures();
    }
  };

  const redo = () => {
    if (onRedoFeatures) {
      onRedoFeatures();
    }
  };

  const addRect = () => {
    const w = Math.max(1, Math.round(Number(width)));
    const d = Math.max(1, Math.round(Number(depth)));
    if (!(w > 0 && d > 0)) {
      return;
    }
    const o = path[0] ?? { x: 0, y: 0 };
    onCommit([o, { x: o.x + w, y: o.y }, { x: o.x + w, y: o.y + d }, { x: o.x, y: o.y + d }]);
    setPath([]);
  };

  const zoomAtCenter = factor => {
    const el = wrap.current;
    const cx = (el?.clientWidth ?? view.w) / 2;
    const cy = (el?.clientHeight ?? view.h) / 2;
    const ftX = (cx - origin.x) / pxPerFt;
    const ftY = (origin.y - cy) / pxPerFt;
    const next = Math.min(28, Math.max(2, pxPerFt * factor));
    setPxPerFt(next);
    setOrigin({ x: cx - ftX * next, y: cy + ftY * next });
  };

  const onWheel = e => {
    e.preventDefault();
    zoomAtCenter(e.deltaY < 0 ? 1.1 : 0.9);
  };

  const grid = useMemo(() => {
    const lines = [];
    const w = 1800;
    const h = 1400;
    const ft0x = Math.floor(-origin.x / pxPerFt) - 2;
    const ft0y = Math.floor(-(h - origin.y) / pxPerFt) - 2;
    const ft1x = Math.ceil((w - origin.x) / pxPerFt) + 2;
    const ft1y = Math.ceil(origin.y / pxPerFt) + 2;
    for (let x = ft0x; x <= ft1x; x++) {
      const sx = origin.x + x * pxPerFt;
      lines.push({ x1: sx, y1: 0, x2: sx, y2: h, major: x % 10 === 0 });
    }
    for (let y = ft0y; y <= ft1y; y++) {
      const sy = origin.y - y * pxPerFt;
      lines.push({ x1: 0, y1: sy, x2: w, y2: sy, major: y % 10 === 0 });
    }
    return lines;
  }, [origin, pxPerFt]);

  const glaLive = Math.round(
    features.reduce((s, f) => {
      const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
      const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
      return s + livingSqft(f, typeCode, garageKindName);
    }, 0),
  );

  const applyEdit = e => {
    const cur = editRef.current;
    if (!cur) {
      return;
    }
    const raw = toFeet(e.clientX, e.clientY);
    if (cur.kind === 'pan') {
      setOrigin({
        x: cur.ox + (e.clientX - cur.sx),
        y: cur.oy + (e.clientY - cur.sy),
      });
      return;
    }
    if (cur.kind === 'draft') {
      setPath(p => {
        if (!p[cur.index]) {
          return p;
        }
        const next = [...p];
        const prev = next[cur.index - 1] ?? next[cur.index];
        next[cur.index] = roundPt(orthoSnap(prev, raw));
        return next;
      });
      return;
    }
    if (cur.kind === 'pending') {
      if (Math.hypot(e.clientX - cur.sx, e.clientY - cur.sy) < 12) {
        return;
      }
      if (cur.mode === 'vertex') {
        setEdit({ kind: 'vertex', id: cur.id, index: cur.index, orig: cur.orig });
        if (onMoveStart) {
          onMoveStart(cur.id);
        }
        const snapped = roundPt(raw);
        onMove(
          cur.id,
          cur.orig.map((p, i) => (i === cur.index ? snapped : p)),
        );
      } else {
        setEdit({ kind: 'edge', id: cur.id, index: cur.index, start: cur.start, orig: cur.orig });
        if (onMoveStart) {
          onMoveStart(cur.id);
        }
        onMove(cur.id, stretchEdge(cur.orig, cur.index, cur.start, raw));
      }
      return;
    }
    if (cur.kind === 'move') {
      // Body drags enter 'move' immediately on pointerdown (before any movement is confirmed),
      // unlike the 'pending'->vertex/edge transition above which is already gated by a movement
      // threshold — so this only calls onMoveStart once, on the first actual drag frame, via a
      // flag mutated directly on the (stable, per-drag) edit object rather than through setEdit,
      // matching the mobile app's movingRef-guarded onMove pattern.
      if (!cur.historyPushed) {
        cur.historyPushed = true;
        if (onMoveStart) {
          onMoveStart(cur.id);
        }
      }
      const dx = Math.round(raw.x - cur.start.x);
      const dy = Math.round(raw.y - cur.start.y);
      onMove(
        cur.id,
        cur.orig.map(p => ({ x: p.x + dx, y: p.y + dy })),
      );
      return;
    }
    if (cur.kind === 'vertex') {
      const snapped = roundPt(raw);
      const next = cur.orig.map((p, i) => (i === cur.index ? snapped : p));
      onMove(cur.id, next);
      return;
    }
    if (cur.kind === 'edge') {
      onMove(cur.id, stretchEdge(cur.orig, cur.index, cur.start, raw));
    }
  };

  return (
    <div className={classes.container}>
      <div
        ref={wrap}
        className={classes.canvasArea}
        onWheel={onWheel}
        onPointerDown={e => {
          if (e.target.closest?.('[data-ui]')) {
            return;
          }
          if (e.button === 1 || e.shiftKey || e.altKey) {
            e.currentTarget.setPointerCapture(e.pointerId);
            setEdit({ kind: 'pan', sx: e.clientX, sy: e.clientY, ox: origin.x, oy: origin.y });
            return;
          }
          if (e.button !== 0) {
            return;
          }
          const raw = toFeet(e.clientX, e.clientY);

          if (intent === 'pan') {
            e.currentTarget.setPointerCapture(e.pointerId);
            setEdit({ kind: 'pan', sx: e.clientX, sy: e.clientY, ox: origin.x, oy: origin.y });
            return;
          }

          if (path.length > 0) {
            const lastPt = path[path.length - 1];
            const s = screen(lastPt);
            const rect = wrap.current?.getBoundingClientRect();
            const x = e.clientX - (rect?.left ?? 0);
            const y = e.clientY - (rect?.top ?? 0);
            if (Math.hypot(x - s.x, y - s.y) <= 16) {
              e.currentTarget.setPointerCapture(e.pointerId);
              setEdit({ kind: 'draft', index: path.length - 1 });
              return;
            }
            if (path.length >= 3) {
              const first = screen(path[0]);
              if (Math.hypot(x - first.x, y - first.y) <= 18) {
                close();
                return;
              }
            }
            const attach = snapToExisting(features, raw, pxPerFt);
            const next = attach ?? roundPt(orthoSnap(lastPt, raw));
            if (next.x === lastPt.x && next.y === lastPt.y) {
              return;
            }
            if (path.length >= 3 && Math.hypot(next.x - path[0].x, next.y - path[0].y) <= 2.5) {
              close();
              return;
            }
            setPath(p => [...p, next]);
            return;
          }

          if (intent === 'edit') {
            const hit = hitFeature(features, screen, raw, pxPerFt, selectedId);
            if (hit) {
              e.currentTarget.setPointerCapture(e.pointerId);
              const orig = [...(features.find(f => f.id === hit.id)?.vertices ?? [])];
              if (hit.kind === 'body') {
                onSelect(hit.id);
                setEdit({ kind: 'move', id: hit.id, start: raw, orig });
                return;
              }
              onSelect(hit.id);
              setEdit({
                kind: 'pending',
                id: hit.id,
                mode: hit.kind,
                index: hit.index,
                start: raw,
                orig,
                snap: hit.snap,
                sx: e.clientX,
                sy: e.clientY,
              });
              return;
            }
            onSelect(null);
            e.currentTarget.setPointerCapture(e.pointerId);
            setEdit({ kind: 'pan', sx: e.clientX, sy: e.clientY, ox: origin.x, oy: origin.y });
            return;
          }

          if (intent === 'attach') {
            const selectedFeature = features.find(f => f.id === selectedId);
            const snap = selectedFeature ? snapToExisting([selectedFeature], raw, pxPerFt) : null;
            if (snap) {
              setPath([snap]);
              setIntent('draw');
            }
            return;
          }

          // intent === 'draw' — existing shapes are purely visual here (not hit-tested),
          // matching Pan/Attach being inert modes for editing; only Edit mode can select or
          // reshape an existing feature.
          setPath([roundPt(raw)]);
        }}
        onPointerMove={applyEdit}
        onPointerUp={() => {
          setEdit(null);
        }}
      >
        <svg data-sketch-grid="1" className={classes.svg} width={view.w} height={view.h}>
          {grid.map((g, i) => (
            <line
              key={i}
              x1={g.x1}
              y1={g.y1}
              x2={g.x2}
              y2={g.y2}
              stroke={g.major ? '#2c3340' : '#1a1f28'}
              strokeWidth={g.major ? 1.2 : 0.6}
            />
          ))}
          {features.map(f => {
            const typeCode = getFeatureTypeCode(f.tapeFeatureTypeId, tapeConfig);
            const garageKindName = getGarageKindName(f.tapeGarageKindId, tapeConfig);
            const foundationCode = (tapeConfig?.foundationTypes || []).find(x => x.id === f.tapeFoundationTypeId)?.code || '';
            const pts = (f.vertices || []).map(screen);
            const d = `${pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ')} Z`;
            const lens = edgeLengths(f.vertices || []);
            const others = features.filter(x => x.id !== f.id).map(x => (x.vertices || []).map(screen));
            const c = labelAnchor(pts, others);
            const fp = Math.round(measureSqft(f.vertices));
            const liv = Math.round(livingSqft(f, typeCode, garageKindName));
            const selected = f.id === selectedId;
            const color = TYPE_COLORS[typeCode] || TYPE_COLORS.OTH;
            return (
              <g key={f.id} data-feat={f.id}>
                <path d={d} fill={color} fillOpacity={selected ? 0.4 : 0.22} stroke={color} strokeWidth={selected ? 2.5 : 1.75} />
                {(f.vertices || []).map((a, i) => {
                  const b = (f.vertices || [])[(i + 1) % (f.vertices?.length ?? 1)];
                  const mid = screen({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
                  return (
                    <g key={i}>
                      <text x={mid.x} y={mid.y - 10} fill="#d8d4c8" fontSize="11" fontFamily="IBM Plex Mono, monospace" textAnchor="middle">
                        {`${Math.round(lens[i])}'`}
                      </text>
                      {selected && <circle cx={mid.x} cy={mid.y} r="5" fill="#ecece8" stroke="#0e1012" strokeWidth="1.25" />}
                    </g>
                  );
                })}
                {selected &&
                  pts.map((p, i) => (
                    <rect key={`v${i}`} x={p.x - 5} y={p.y - 5} width="10" height="10" fill="#ecece8" stroke="#0e1012" strokeWidth="1.25" />
                  ))}
                <text x={c.x} y={c.y} textAnchor="middle" dominantBaseline="middle" fill="#ecece8" fontFamily="IBM Plex Sans, sans-serif">
                  <tspan x={c.x} dy="-0.65em" fontSize="12" fontWeight="600">
                    {sectionAbbrev(f, typeCode, foundationCode, garageKindName)}
                  </tspan>
                  <tspan x={c.x} dy="1.4em" fontSize="11" fontFamily="IBM Plex Mono, monospace">
                    {liv ? `${liv.toLocaleString()} GLA` : `${fp.toLocaleString()} SF`}
                  </tspan>
                </text>
              </g>
            );
          })}
          {path.length > 0 && (
            <g>
              <polyline
                fill="none"
                stroke="#c8ccd4"
                strokeWidth="2"
                points={path
                  .map(p => {
                    const s = screen(p);
                    return `${s.x},${s.y}`;
                  })
                  .join(' ')}
              />
              {path.map((p, i) => {
                const s = screen(p);
                const lastPt = i === path.length - 1;
                return (
                  <circle
                    key={i}
                    cx={s.x}
                    cy={s.y}
                    r={lastPt ? 7 : i === 0 ? 5 : 3.5}
                    fill={lastPt ? '#ecece8' : '#c8ccd4'}
                    stroke="#0e1012"
                    strokeWidth={lastPt ? 1.5 : 0}
                  />
                );
              })}
              {path.length > 1 &&
                path.slice(0, -1).map((a, i) => {
                  const b = path[i + 1];
                  const mid = screen({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
                  const len = Math.hypot(b.x - a.x, b.y - a.y);
                  return (
                    <text key={i} x={mid.x} y={mid.y - 8} fill="#ecece8" fontSize="12" fontFamily="IBM Plex Mono, monospace" textAnchor="middle">
                      {`${Math.round(len)}'`}
                    </text>
                  );
                })}
            </g>
          )}
        </svg>
        <div data-ui="zoom" className={classes.zoomBar}>
          <IconButton size="small" onPointerDown={e => e.stopPropagation()} onClick={() => zoomAtCenter(1.25)} aria-label="Zoom in">
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onPointerDown={e => e.stopPropagation()} onClick={() => zoomAtCenter(0.8)} aria-label="Zoom out">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </div>
        <div className={classes.topOverlay}>
        <div className={classes.hud}>
          {path.length >= 2
            ? `${Math.round(
                Math.hypot(path[path.length - 1].x - path[path.length - 2].x, path[path.length - 1].y - path[path.length - 2].y),
              )}'`
            : intent === 'pan'
              ? 'Pan'
              : intent === 'edit'
                ? 'Edit — drag a vertex, edge, or shape'
                : intent === 'attach'
                  ? 'Tap a wall'
                  : 'Draw'}
          {glaLive ? `  ·  ${glaLive.toLocaleString()} GLA` : ''}
        </div>
        </div>
        {selectedId &&
          (() => {
            const sel = features.find(f => f.id === selectedId);
            const pts = (sel?.vertices ?? []).map(screen);
            if (pts.length < 3) {
              return null;
            }
            const left = Math.max(...pts.map(p => p.x)) + 6;
            const top = Math.min(...pts.map(p => p.y)) - 50;
            return (
              <IconButton
                data-ui="delete"
                aria-label="Delete section"
                onPointerDown={e => e.stopPropagation()}
                onClick={e => {
                  e.stopPropagation();
                  onDelete(selectedId);
                }}
                className={classes.deleteBtn}
                style={{ left, top }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            );
          })()}
      </div>

      <TapeDrawPad
        modeBar={
          <div className={classes.modeCols}>
            <div className={classes.modeCol}>
              <Button
                size="small"
                variant="outlined"
                className={cn(classes.intentButton, intent === 'pan' && classes.intentButtonActive)}
                onClick={() => {
                  setIntent('pan');
                  onSelect(null);
                }}
                startIcon={<PanToolIcon fontSize="small" />}
                title="Pan (P)"
              >
                Pan
              </Button>
              <Button
                size="small"
                variant="outlined"
                className={cn(classes.intentButton, intent === 'edit' && classes.intentButtonActive)}
                onClick={() => setIntent('edit')}
                startIcon={<OpenWithIcon fontSize="small" />}
                title="Edit (E)"
              >
                Edit
              </Button>
            </div>
            <div className={classes.modeCol}>
              <Button
                size="small"
                variant="outlined"
                className={cn(classes.intentButton, intent === 'draw' && classes.intentButtonActive)}
                onClick={() => {
                  setIntent('draw');
                  onSelect(null);
                }}
                startIcon={<CreateIcon fontSize="small" />}
                title="Draw (D)"
              >
                Draw
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedId}
                className={cn(classes.intentButton, intent === 'attach' && classes.intentButtonActive)}
                onClick={() => setIntent('attach')}
                startIcon={<AddIcon fontSize="small" />}
                title="Add onto wall (A)"
              >
                Add onto wall
              </Button>
            </div>
          </div>
        }
        dir={dir}
        onDir={setDir}
        length={length}
        onLength={setLength}
        width={width}
        depth={depth}
        onWidth={setWidth}
        onDepth={setDepth}
        onAddWall={addWall}
        onUndo={undo}
        onRedo={redo}
        onClose={close}
        canClose={path.length >= 3}
        onPlaceRect={addRect}
        onClear={() => {
          setPath([]);
          onClearFeatures();
        }}
      />
    </div>
  );
};

TapeCanvas.propTypes = {
  features: PropTypes.array,
  selectedId: PropTypes.string,
  tapeConfig: PropTypes.object,
  onCommit: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onMoveStart: PropTypes.func,
  onClearFeatures: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUndoFeatures: PropTypes.func,
  onRedoFeatures: PropTypes.func,
};

export default TapeCanvas;
